import ModalContainer from 'components/modal/modal-container'

import ModalActions from 'components/modal/modal-actions'
import { Button } from '@mui/material'

import { closeModal } from 'actions/app'

import { archiveFarms } from 'api/farm'
import { useTranslation } from 'react-i18next'

import type { AppState } from 'state/index'
import { useDispatch, useSelector } from 'react-redux'
import { get, find } from 'es-toolkit/compat'
import { useEffect, useState } from 'react'

interface ArchiveFarmProps {
  selectedFarms: {
    id: number | string
    name: string
    clientId: number | string
    fieldCount: number
  }[]
  onSuccess?: () => void
}

export default function ArchiveFarm(props: ArchiveFarmProps) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { selectedFarms, onSuccess } = props

  const farms = useSelector((state) =>
    get(state as AppState, 'farm.collection', []),
  )

  const defaultFarm = useSelector(
    (state) => (state as AppState).account.singleton.prefs?.cff?.farm,
  )

  const [defaultDeletionMsg, showDefaultDeletionMsg] = useState(false)
  const [showActiveFieldsMsg, setShowActiveFieldsMsg] = useState(false)

  useEffect(() => {
    if (
      (defaultFarm &&
        selectedFarms.find(
          //@ts-expect-error
          (farm) => parseInt(defaultFarm) === parseInt(farm.id),
        )) ||
      farms.length === 1
    ) {
      showDefaultDeletionMsg(true)
    }
  }, [selectedFarms, defaultFarm, farms])

  useEffect(() => {
    let _showActiveFieldsMsg = false
    for (let i = 0; i < selectedFarms.length && !_showActiveFieldsMsg; i++) {
      _showActiveFieldsMsg = selectedFarms[i].fieldCount > 0
    }
    setShowActiveFieldsMsg(_showActiveFieldsMsg)
  }, [selectedFarms])

  const closePopup = () => dispatch(closeModal())

  const archiveItem = () => {
    const farmIds = selectedFarms.map((farm) => farm.id)
    // There can be active fields in the farm that do not show up if they have no boundary. To handle this case, we
    // cascade the archive action to the fields when archiving a farm, if the field count is 0 to handle this case.
    // Otherwise the user will need to archive the fields separately before being able to archive the farm.
    //@ts-expect-error
    dispatch(archiveFarms({ farmIds, cascade: !showActiveFieldsMsg })).then(
      () => {
        if (onSuccess) {
          onSuccess()
        }
      },
    )
  }

  return (
    <ModalContainer>
      {defaultDeletionMsg ? (
        <>
          <div>
            {t('cannot_delete_default', {
              entity: t('farm'),
            })}
          </div>
          <ModalActions>
            <Button variant="contained" color="primary" onClick={closePopup}>
              {t('ok')}
            </Button>
          </ModalActions>
        </>
      ) : showActiveFieldsMsg ? (
        <>
          {selectedFarms.map((farm) => {
            if (farm.fieldCount > 0) {
              return (
                <div key={farm.id}>
                  {t('delete_child_entity_first', {
                    parentEntityName: farm.name,
                    childEntity: t('fields'),
                    parentEntity: t('farm'),
                  })}
                </div>
              )
            }
          })}

          <ModalActions>
            <Button variant="contained" color="primary" onClick={closePopup}>
              {t('ok')}
            </Button>
          </ModalActions>
        </>
      ) : (
        <>
          <div>
            {t('archive_item_warning', {
              item: selectedFarms.length > 1 ? t('farms') : t('farm'),
            })}
          </div>
          <br />
          <ModalActions>
            <Button variant="contained" color="secondary" onClick={closePopup}>
              {t('cancel')}
            </Button>
            <Button variant="contained" color="primary" onClick={archiveItem}>
              {t('archive')}
            </Button>
          </ModalActions>
        </>
      )}
    </ModalContainer>
  )
}
